// One round trip for a whole session (ADR-0027).
//
// It replaces `my_access_state()` + `my_workspaces()` + `my_invitations()` + the
// four onboarding counts — seven calls in three sequential stages — with one.
//
// The reason is not the round trips. It is that those three calls had a
// REQUIRED ORDER that lived nowhere on the server: admission is what provisions
// (ADR-0023), so the gate must run before the workspace list is read. That rule
// was a comment in two client files, and both clients broke it, which is how a
// brand new user came to land in an app that told them they were a member of
// nothing. On the server, in one transaction, there is no order left to get
// wrong — and no way to assemble a snapshot from three different instants.

import { supabase } from "./supabase";
import { asAppError } from "./entitlements";
import type { Access } from "./access";
import type { WorkspaceSummary } from "./workspace";
import type { PendingInvitation } from "./people";
import type { ServerProgress } from "../engagement/progress";

export interface SessionSnapshot {
  access: Access;
  workspaces: WorkspaceSummary[];
  /**
   * The workspace the server actually used: the id we asked for, or a fallback
   * when that one is no longer ours. Read it back rather than assuming — a
   * remembered id survives a revoked membership, and this is where we find out.
   */
  active: string | null;
  invitations: PendingInvitation[];
  /** Progress for `active`. Null when there is no workspace to have any. */
  progress: ServerProgress | null;
}

export async function loadSession(workspaceId: string | null): Promise<SessionSnapshot> {
  const { data, error } = await supabase.rpc("my_session", {
    p_workspace_id: workspaceId,
  });
  if (error) throw asAppError(error);

  const row = (data ?? {}) as Record<string, any>;
  const access = (row.access ?? {}) as Record<string, any>;

  return {
    access: {
      state: access.state ?? "anonymous",
      via: access.via ?? null,
      waitlistStatus: access.waitlist_status ?? null,
      waitlistPosition: access.waitlist_position ?? null,
    },
    workspaces: (row.workspaces ?? []) as WorkspaceSummary[],
    active: (row.active ?? null) as string | null,
    invitations: (row.invitations ?? []) as PendingInvitation[],
    progress: (row.progress ?? null) as ServerProgress | null,
  };
}
